import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import type { Order } from '../types/@types/order'
import { getOrders, updateOrderStatus } from '../services/OrderService'

export function useOrders() {
  return useQuery<Order[]>({
    queryKey: ['orders'],
    queryFn: () => getOrders(),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  })
}

export function useOrderStatusMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    // 'PAID' se usa como señal de pago; el backend lo mapea a payment_status internamente
    mutationFn: ({ id, status }: { id: string | number; status: string }) =>
      updateOrderStatus(String(id), status as any),
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: ['orders'] })
      const previous = queryClient.getQueryData<Order[]>(['orders'])
      queryClient.setQueryData<Order[]>(['orders'], old =>
        old
          ? old.map(o =>
              String(o.id) === String(id)
                ? { ...o, status: status as Order['status'] }
                : o,
            )
          : old,
      )
      return { previous }
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['orders'], ctx.previous)
      const error = err as AxiosError<{ message?: string }>
      toast.error(error.response?.data?.message || 'Error en la solicitud')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] })
    },
  })
}
